import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Modal from '../../../components/Modal/Modal';
import CrossLoader from '../../../components/CrossLoader/CrossLoader';
import ImageWrapper from '../../../components/ImageWrapper/ImageWrapper';
import Button from '../../../components/Button/Button';
import Activity from '../../../helpers/Activity';
import Debug from '../../../helpers/Debug';
import Navigation from '../../../config/Navigation';
import {firestore, getCollectionPath} from '../../../config/Firebase';
import {doc, getDoc} from 'firebase/firestore';
import {productConverter} from '../../../models/Product';
import errorIcon from './images/error.png';
import './ProductValidationModal.css';

function ProductValidationModal({scanResult, onClose}) {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [urlError, setUrlError] = useState(false);
    const [invalidUrlError, setInvalidUrlError] = useState(false);
    const [invalidPathError, setInvalidPathError] = useState(false);
    const [invalidProductError, setInvalidProductError] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            validateScanResult();
        }, 1500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateScanResult = () => {
        const _isUrl = isUrl(scanResult);
        if (!_isUrl) {
            setUrlError(true);
            Activity.log('product-validation-modal', 'URL', 'validate', {status: 'failed', error: 'InvalidURL', scan_result: scanResult});
            return;
        }
        const _validUrl = validUrl(scanResult);
        if (!_validUrl) {
            setInvalidUrlError(true);
            Activity.log('product-validation-modal', 'URL', 'validate', {status: 'failed', error: 'InvalidHost', scan_result: scanResult});
            return;
        }
        const _validPath = validPath(scanResult);
        if (!_validPath) {
            setInvalidPathError(true);
            Activity.log('product-validation-modal', 'URL', 'validate', {status: 'failed', error: 'InvalidPath', scan_result: scanResult});
            return;
        }
        validProduct(scanResult)
            .then((_validProduct) => {
                if (_validProduct) {
                    redirectToProduct();
                    Activity.log('product-validation-modal', 'URL', 'validate', {status: 'success', error: null, scan_result: scanResult});
                } else {
                    setInvalidProductError(true);
                    Activity.log('product-validation-modal', 'URL', 'validate', {status: 'failed', error: 'InvalidProduct', scan_result: scanResult});
                }
            })
    };

    useEffect(() => {
        if (urlError || invalidUrlError || invalidPathError || invalidProductError) setIsLoading(false);
    }, [urlError, invalidUrlError, invalidPathError, invalidProductError]);

    const isUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (error) {
            Debug.printToLog('error', error);
            return false;
        }
    };

    const validUrl = (string) => {
        try {
            const url = new URL(string);
            const sameProtocol = url.protocol === location.protocol;
            const sameHost = url.host === location.host;
            return sameProtocol && sameHost;
        } catch (error) {
            Debug.printToLog('error', error);
            return false;
        }
    };

    const validPath = (string) => {
        const productId = getProductId(string);
        return !!productId;
    };

    const validProduct = (string) => {
        return new Promise((resolve) => {
            const productId = getProductId(string);
            const _path = getCollectionPath('items');
            const _doc = doc(firestore, _path, productId).withConverter(productConverter);
            getDoc(_doc)
                .then((_document) => {
                    resolve(_document.exists());
                })
                .catch((error) => {
                    Debug.printToLog('error', error);
                    resolve(false);
                });
        });
    };

    const getProductId = (string) => {
        try {
            const url = new URL(string);
            const match = url.pathname.match(/^\/product\/([a-zA-Z0-9]+)\/?$/);
            return match ? match[1] : undefined;
        } catch (error) {
            Debug.printToLog('error', error);
            return undefined;
        }
    };

    const getStoreId = (string) => {
        try {
            const url = new URL(string);
            return url.searchParams.get('oda');
        } catch (error) {
            Debug.printToLog('error', error);
            return undefined;
        }
    };

    const getSource = (string) => {
        try {
            const url = new URL(string);
            return url.searchParams.get('omc');
        } catch (error) {
            Debug.printToLog('error', error);
            return undefined;
        }
    };

    const getTitle = () => {
        if (isLoading) return 'Validando producto...';
        else if (urlError) return 'Lo sentimos';
        else if (invalidUrlError) return 'Lo sentimos';
        else if (invalidPathError) return 'Lo sentimos';
        else if (invalidProductError) return 'Lo sentimos';
    };

    const redirectToProduct = () => {
        const productId = getProductId(scanResult);
        const storeId = getStoreId(scanResult);
        const source = getSource(scanResult);
        const url = Navigation.getProductUrl(productId, storeId, undefined, source);
        navigate(url);
    };

    const handleClose = () => {
        Activity.log('product-validation-modal', 'error-message', 'close');
        if (onClose) onClose();
    };

    const renderLoader = () => {
        return (
            <div className='product-validation-modal_loader'>
                <CrossLoader size={100}/>
            </div>
        );
    };

    const renderError = (message) => {
        return (
            <div className='product-validation-modal_error'>
                <div className='product-validation-modal_error-wrapper'>
                    <div className='product-validation-modal_error-icon'>
                        <ImageWrapper
                            image={errorIcon}
                            alt='Error'
                            display='block'
                            width={70}
                            height='auto'
                            errorWidth={undefined}
                            errorHeight={undefined}
                            errorPadding={undefined}
                            margin={5}
                            borderRadius={0}
                            transition={true}
                            loadDelay={0.1}
                            onLoad={undefined}
                            onMouseEnter={undefined}
                            onMouseLeave={undefined}
                        />
                    </div>
                    <div className='product-validation-modal_error-message'>
                        {message}
                    </div>
                </div>
                <div className='product-validation-modal_error-buttons'>
                    <div className='product-validation-modal_error-button'>
                        <Button
                            icon={undefined}
                            label='Escanear otro código'
                            labelColor='#002EFF'
                            backgroundColor='#FFFFFF'
                            borderColor='#002EFF'
                            borderWidth={0}
                            borderRadius={50}
                            padding='15px 10px'
                            paddingTarget='label'
                            margin={0}
                            width='100%'
                            minWidth={undefined}
                            maxWidth={undefined}
                            height='auto'
                            minHeight={undefined}
                            maxHeight={undefined}
                            transition='label'
                            fontSize={22}
                            boxShadow='unset'
                            isLoading={false}
                            loadLabel={undefined}
                            disabled={false}
                            showPulseEffect={false}
                            notificationNumber={undefined}
                            notificationBackgroundColor={undefined}
                            notificationLabelColor={undefined}
                            onClick={handleClose}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal title={getTitle()} showCloseButton={false} onClose={handleClose}>
            <div className='product-validation-modal'>
                {isLoading && renderLoader()}
                {!isLoading && urlError && renderError('No se ha encontrado una dirección.')}
                {!isLoading && invalidUrlError && renderError('La dirección proporcionada no es válida.')}
                {!isLoading && invalidPathError && renderError('La estructura de la dirección proporcionada no es válida.')}
                {!isLoading && invalidProductError && renderError('No se ha encontrado el producto especificado.')}
            </div>
        </Modal>
    );
}

export default ProductValidationModal;